import { useRef } from 'react';

import useNotify from '@hooks/notify';

import CustomTableHeader from './CustomTableHeader';
import CustomTablePagination from './CustomtablePagination';
import CustomTableRow from './CustomTableRow';
import { CustomTableHeaderProps } from './types';

interface CustomTableProps {
  headers: CustomTableHeaderProps[];
  data: {
    [key: string]: string | number | boolean | React.ReactNode;
  }[];
  handleSelect?: (row: any) => void;
  totalCount?: number;
  onPageChange?: (count: number) => void;
  currentPage?: number;
  hasMultiSelect?: boolean;
  handleSelectAll?: (selectedIds: string[], removedIds: string[]) => void;
  selectedRows?: (string | number)[];
  handleCheckboxSelect?: (row: any) => void;
  maxLimit?: number;
  disabledRowIds?: string[];
  hasInfiniteScroll?: boolean;
  className?: string;
}

const CustomTable = ({
  headers,
  data,
  handleSelect,
  totalCount,
  onPageChange,
  currentPage,
  hasMultiSelect,
  handleSelectAll,
  selectedRows,
  handleCheckboxSelect,
  maxLimit,
  disabledRowIds,
  hasInfiniteScroll = false,
  className = ''
}: CustomTableProps) => {
  const { createErrorAlert } = useNotify();

  const infiniteScrollRef = useRef<HTMLDivElement>(null);

  const showPagination = totalCount && totalCount > 0;

  const isSelectionLimitReached = (itemCount = 1) =>
    maxLimit && selectedRows && selectedRows.length + itemCount > maxLimit;

  const handleSelectAllCheckbox = (checked: boolean) => {
    // Filter out the selected rows
    const filteredData = checked
      ? data
          .filter((item) => !selectedRows?.includes(item.id as string))
          .filter((item) => !disabledRowIds?.includes(item.id as string))
      : data
          .filter((item) => selectedRows?.includes(item.id as string))
          .filter((item) => !disabledRowIds?.includes(item.id as string));

    if (checked) {
      if (isSelectionLimitReached(filteredData.length)) {
        createErrorAlert('Limit reached');
        return;
      }
      handleSelectAll?.(
        filteredData.map((item) => item.id as string),
        []
      );
    } else {
      handleSelectAll?.(
        [],
        filteredData.map((item) => item.id as string)
      );
    }
  };

  const handleCheckboxSelectCustom = (row: any) => {
    if (disabledRowIds?.includes(row.id)) return;
    if (isSelectionLimitReached() && !selectedRows?.includes(row.id))
      createErrorAlert('Limit reached');
    else handleCheckboxSelect?.(row);
  };

  const handleSelectCustom = (row: any) => {
    if (disabledRowIds?.includes(row.id)) return;
    if (isSelectionLimitReached() && !selectedRows?.includes(row.id))
      createErrorAlert('Limit reached');
    else handleSelect?.(row);
  };

  const isAllRowSelected = () =>
    data
      .filter((item) => !disabledRowIds?.includes(item.id as string))
      .every((item) => selectedRows?.includes(item.id as string));

  return (
    <div className={`${showPagination ? 'pb-4' : 'pb-18'} ${className}`}>
      <CustomTableHeader
        headers={headers}
        hasMultiSelect={hasMultiSelect}
        handleSelectAll={handleSelectAllCheckbox}
        isAllRowSelected={isAllRowSelected()}
      />
      <CustomTableRow
        headers={headers}
        data={data}
        handleSelect={handleSelectCustom}
        hasMultiSelect={hasMultiSelect}
        selectedRows={selectedRows}
        disabledRowIds={disabledRowIds}
        handleCheckboxSelect={handleCheckboxSelectCustom}
      />
      {showPagination &&
        (hasInfiniteScroll ? (
          <div ref={infiniteScrollRef} className="h-1" />
        ) : (
          <CustomTablePagination
            currentPage={currentPage || 1}
            totalCount={totalCount || 0}
            onPageChange={onPageChange || (() => {})}
            isFixed={false}
          />
        ))}
    </div>
  );
};

export default CustomTable;
